import { Outlet, createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { ThemeProvider } from 'styled-components'
import { AppLoader } from '~/components/loader'
import { setLastOrgAlias } from '~/features/auth/utils'
import { DatastoreProvider } from '~/features/datastore'
import GlobalStyles from '~/globalStyles.styles'
import { useCurrentUserQuery, useLegacyTheme } from '~/hooks'
import { PageLevelError, ServiceUnavailableError } from '~/modules/error-boundary'
import Loading, { LoadingProvider } from '~/modules/loader'
import { isServiceUnavailableError } from '~/services/trpc'

import { Button, Dialog, DialogContent } from '@hap/components'

import { CompleteProfileGate } from './$alias/-components/complete-profile-gate'

export const Route = createFileRoute('/_authenticated/$alias')({
  component: RouteComponent,
})

function RouteComponent() {
  const { alias } = Route.useParams()
  const theme = useLegacyTheme()
  const currentUserQuery = useCurrentUserQuery()
  const currentUser = currentUserQuery.data

  useEffect(() => {
    setLastOrgAlias(alias)
  }, [alias])
  const needsProfileCompletion = !!(
    currentUser &&
    (!currentUser.firstName?.trim() || !currentUser.lastName?.trim())
  )

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <DatastoreProvider>
        <LoadingProvider>
          <Loading />
          {isServiceUnavailableError(currentUserQuery.error) ? (
            <ServiceUnavailableError
              isRetrying={currentUserQuery.isFetching}
              onRetry={() => void currentUserQuery.refetch()}
            />
          ) : currentUserQuery.error ? (
            <PageLevelError
              title="Unable to load your profile"
              description="Please try again. If the problem continues, contact support."
              actions={
                <Button
                  loading={currentUserQuery.isFetching}
                  onClick={() => void currentUserQuery.refetch()}
                >
                  Try again
                </Button>
              }
            />
          ) : !currentUser ? (
            <AppLoader />
          ) : (
            <>
              <Outlet />
              <Dialog open={needsProfileCompletion}>
                <DialogContent
                  onEscapeKeyDown={(event) => event.preventDefault()}
                  onPointerDownOutside={(event) => event.preventDefault()}
                  onInteractOutside={(event) => event.preventDefault()}
                  className="max-w-lg p-0"
                >
                  <CompleteProfileGate
                    userId={currentUser.id}
                    firstName={currentUser.firstName || ''}
                    lastName={currentUser.lastName || ''}
                  />
                </DialogContent>
              </Dialog>
            </>
          )}
        </LoadingProvider>
      </DatastoreProvider>
    </ThemeProvider>
  )
}
